/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    AsyncResult,
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICommerceApiSettings,
    IDictionary
} from '@msdyn365-commerce/core';
import { DataServiceQuery, executeAction, IContext, IDataServiceRequest, IQueryResultSettings } from '@msdyn365-commerce/retail-proxy';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import { getProductImageUrls } from '../utils/product-list-utils';

const buildCacheKey = (base: string, apiSettings: ICommerceApiSettings, catalogId: Number | null): string => {
    return `${base}-chanId:${apiSettings.channelId}-catId:${catalogId}`;
};

/**
 * Product Input
 */
export class ProductInput implements IActionInput {
    public productId: number;
    public channelId: number;
    public catalogId: number | null;
    private apiSettings: ICommerceApiSettings;

    constructor(productId: number | string, apiSettings: ICommerceApiSettings, catalogId: number | null) {
        this.apiSettings = apiSettings;
        this.productId = +productId;
        this.channelId = apiSettings.channelId;
        this.catalogId = catalogId;
    }

    public getCacheKey = () => buildCacheKey(`RecordId-${this.productId.toString()}`, this.apiSettings, this.catalogId);
    public getCacheObjectType = () => 'SimpleProduct';
    public dataCacheType = (): CacheType => 'application';
}

function productsQuery(recordId?: number): DataServiceQuery {
    const key = recordId ? { RecordId: recordId } : null;
    return new DataServiceQuery('Products', 'Product', 'ProductClass', key);
}

function createGetByIdsInput(
    queryResultSettings: IQueryResultSettings,
    channelId: number,
    productIds: number[],
    catalogId: number | null
): IDataServiceRequest {
    const query = productsQuery().resultSettings(queryResultSettings);
    return query.createDataServiceRequestForOperation(
        'GetByIds',
        true,
        'SimpleProductClass',
        'true',
        { bypassCache: 'none', returnEntity: 'SimpleProduct' },
        { channelId: channelId, productIds: productIds, catalogId: catalogId }
    );
}

function getByIdsAsyncAction(
    context: IContext,
    channelId: number,
    productIds: number[],
    catalogId: number | null
): AsyncResult<SimpleProduct[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const request = createGetByIdsInput(<any>context.queryResultSettings, channelId, productIds, catalogId);
    return executeAction<SimpleProduct[]>(request, context);
}

/**
 * Calls the Retail API and returns the product based on the passed ProductInput
 */
export async function getSimpleProductsAction(inputs: ProductInput[], ctx: IActionContext): Promise<SimpleProduct[]> {
    if (!inputs || !Array.isArray(inputs) || inputs.length === 0) {
        ctx.trace('[getSimpleProductsAction] Invalid or empty inputs passed.');
        return [];
    }

    const productIdMapping: IDictionary<number> = {};
    const productIds = inputs.map((input, idx) => {
        productIdMapping[input.productId] = idx;
        return input.productId;
    });

    return getByIdsAsyncAction({ callerContext: ctx }, inputs[0].channelId, productIds, inputs[0].catalogId).then(products => {
        if (!products) {
            return [];
        }
        const mappedProducts = (<SimpleProduct[]>getProductImageUrls(products, ctx.requestContext.apiSettings)).reduce(
            (memo: SimpleProduct[], product: SimpleProduct | undefined) => {
                if (!product) {
                    return memo;
                }
                const idx = productIdMapping[product.RecordId];
                memo[idx] = product;
                return memo;
            },
            []
        );

        // NOTE: <ZAFINE> This is not an ideal fix, but will resolve all current issues
        return inputs.map(input => {
            const foundProduct = mappedProducts.find(product => product && product.RecordId === input.productId);
            return foundProduct || <SimpleProduct>{};
        });
    });
}

export default createObservableDataAction({
    action: <IAction<SimpleProduct[]>>getSimpleProductsAction,
    id: '@msdyn365-commerce/get-simple-products',
    isBatched: true
});
